import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Crosshair } from 'lucide-react';
import { getTheme } from '../App';
import { useWS, Strategy, SymbolSpec } from '../WSContext';

const ENTRY_CONDITIONS = [
  { value: 'none',       label: 'Koşul Yok' },
  { value: 'funding_gt', label: 'Funding Rate >' },
  { value: 'funding_lt', label: 'Funding Rate <' },
  { value: 'spread_gt',  label: 'Spread >' },
  { value: 'price_gt',   label: 'Fiyat >' },
  { value: 'price_lt',   label: 'Fiyat <' },
];
const PRICE_TYPES = [
  { value: 'best_bid',       label: 'Best Bid' },
  { value: 'best_ask',       label: 'Best Ask' },
  { value: 'best_direction', label: 'Yöne Göre' },
  { value: 'mid',            label: 'Mid Price' },
  { value: 'mark',           label: 'Mark Price' },
  { value: 'oracle',         label: 'Oracle Fiyatı' },
];

function makeId() { return Math.random().toString(36).slice(2, 10); }
function fmtSym(s: string) { return s.replace('PERP_','').replace('_USDC','').replace('-PERP',''); }

/** Orderly REST API yanıtı (Orderly botlar için fallback) */
interface OrderlySpec { base_min: number; base_tick: number; min_notional: number; base_imr: number; }

function defaultDraft(symbol: string, type: 'standard' | 'tp_only'): Strategy {
  if (type === 'tp_only') {
    return {
      id: makeId(), name: 'TP Only',
      symbol, side: 'long',
      orderSize: 0, orderCount: 3, leverage: 10,
      entryCondition: 'none', entryThreshold: 0,
      priceType: 'best_direction', active: false,
      maxPositionSize: 0,
      tpConfig: { enabled: true, type: 'percent', value: 0.5, mode: 'ladder', triggerPrice: 0, chunkSize: 0, chunkPriceType: 'best_direction' },
      strategyType: 'tp_only',
    };
  }
  return {
    id: makeId(), name: 'Yeni Strateji',
    symbol, side: 'long',
    orderSize: 1, orderCount: 3, leverage: 10,
    entryCondition: 'funding_gt', entryThreshold: 0.001,
    priceType: 'best_direction', active: false,
    maxPositionSize: 0,
    tpConfig: { enabled: false, type: 'percent', value: 0.5 },
    strategyType: 'standard',
  };
}

interface Props {
  dark?: boolean;
  symbol: string;
  onClose: () => void;
}

export default function NewStrategyModal({ dark = true, symbol, onClose }: Props) {
  const { setStrategies, tickers: rawTickers, marketInfo, sendWs, subscribe } = useWS() as any;
  // Perp grupları — stratejinin dolumlarını hangi gruba bağlayacağımızı seçmek için.
  const [groups, setGroups] = React.useState<any[]>([]);
  React.useEffect(() => {
    sendWs?.({ type: 'get_perp_groups', payload: {} });
    const unsub = subscribe?.((m: any) => {
      if (m.type === 'perp_groups') setGroups(m.payload?.list || []);
      // Yeni grup açıldıysa listeyi tazele ve seç
      if (m.type === 'lp_group_created' && m.payload?.groupNo) {
        updateDraftRef.current?.({ groupNo: Number(m.payload.groupNo) });
        sendWs?.({ type: 'get_perp_groups', payload: {} });
      }
    });
    return () => unsub?.();
  }, [sendWs, subscribe]);
  const updateDraftRef = React.useRef<((p: any) => void) | null>(null);
  const t = getTheme(dark);

  const botSymbols: string[] = (rawTickers || []).map((tk: any) => tk.symbol).filter(Boolean);
  const symbolList: string[] = botSymbols.length > 0 ? botSymbols : [symbol];

  const [strategyType, setStrategyType] = useState<'standard' | 'tp_only'>('standard');
  const [draft, setDraft]     = useState<Strategy>(() => defaultDraft(symbol, 'standard'));
  // Orderly API specs (non-aster bots için fallback)
  const [ordSpecs, setOrdSpecs] = useState<Record<string, OrderlySpec>>({});

  // Strateji tipi değişince draft'ı sıfırla (sembolü koru)
  useEffect(() => {
    setDraft(defaultDraft(draft.symbol, strategyType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyType]);

  // Orderly API'den spec çek (marketInfo'da yoksa fallback)
  const fetchOrderlySpec = (sym: string) => {
    if (ordSpecs[sym] || (marketInfo && marketInfo[sym])) return;
    fetch(`https://api.orderly.org/v1/public/info/${sym}`)
      .then(r => r.json())
      .then(d => { if (d?.data) setOrdSpecs(prev => ({ ...prev, [sym]: d.data })); })
      .catch(() => {});
  };

  useEffect(() => { fetchOrderlySpec(symbol); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Kaldıraç kullanıcı tarafından elle değiştirildi mi? Değiştirildiyse sembol
  // değişene kadar otomatik varsayılan ÜSTÜNE YAZMAZ — yazsaydı kullanıcının
  // girdiği değer spec geç geldiğinde sessizce kaybolurdu.
  const levTouchedRef = useRef(false);

  const updateDraft = (patch: Partial<Strategy>) => {
    if (patch.leverage !== undefined) levTouchedRef.current = true;
    if (patch.symbol !== undefined) levTouchedRef.current = false;   // yeni paritede yeniden varsayılan
    setDraft(prev => ({ ...prev, ...patch }));
    if (patch.symbol) fetchOrderlySpec(patch.symbol);
  };
  updateDraftRef.current = updateDraft as any;
  const updateDraftTp = (patch: Partial<Strategy['tpConfig']>) => {
    setDraft(prev => ({ ...prev, tpConfig: { ...prev.tpConfig, ...patch } }));
  };

  const confirmAdd = () => {
    if (!draft.name) return;
    setStrategies((prev: Strategy[]) => [...prev, { ...draft, id: makeId(), active: false }]);
    onClose();
  };

  const inp  = `border rounded-lg px-2 py-1.5 text-xs focus:outline-none w-full ${t.inp}`;
  const s    = draft;

  // Önce WSContext'ten gelen market info (aster), yoksa Orderly API
  const wsSpec: SymbolSpec | undefined   = marketInfo?.[s.symbol];
  const ordSpec: OrderlySpec | undefined = ordSpecs[s.symbol];
  const maxLev  = wsSpec ? wsSpec.max_leverage
                : ordSpec ? Math.floor(1 / ordSpec.base_imr)
                : null;
  const minSize = wsSpec ? wsSpec.min_qty
                : ordSpec ? ordSpec.base_min
                : null;

  // Kaldıraç varsayılanı = PARİTENİN izin verdiği azami. Sabit 10 geliyordu; borsadan
  // borsaya ve pariteden pariteye değiştiği için (Lighter'da SOL 25x, ETH 50x) sabit
  // değer ya boşa riskten kaçırıyor ya da borsanın reddedeceği bir emir üretiyordu.
  // Spec ASENKRON geliyor (marketInfo yayını / Orderly fetch), bu yüzden efekt:
  // spec gelir gelmez bir kez uygulanır, kullanıcı elle dokunduysa dokunulmaz.
  useEffect(() => {
    if (maxLev && maxLev > 0 && !levTouchedRef.current && draft.leverage !== maxLev) {
      setDraft(prev => ({ ...prev, leverage: maxLev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxLev, draft.symbol]);
  const minNotional = wsSpec ? wsSpec.min_notional
                    : ordSpec ? ordSpec.min_notional
                    : null;
  const tickSize = wsSpec ? wsSpec.tick_size
                 : ordSpec ? ordSpec.base_tick
                 : 0.001;
  // MİKTAR ADIMI, fiyat tiki DEĞİL. Emir büyüklüğü input'unun `step`i tickSize
  // idi; farklı iki büyüklük olduğu için adıma uymayan miktar girilebiliyordu.
  // Bluefin böyle bir emri kabul edip hash döndürüyor, sonra sessizce
  // öldürüyor — 07/09'da HYPE-PERP'te adım 0,1 iken 0,69 girildi ve strateji
  // 356 kez boşa denedi, tek bir uyarı çıkmadı.
  const stepSize = wsSpec?.step_size || null;
  const sizeWarn = (() => {
    const q = Number(s.orderSize);
    if (!stepSize || !(stepSize > 0) || !(q > 0)) return null;
    const snapped = Math.floor(q / stepSize + 1e-9) * stepSize;
    const dec = Math.max(0, (String(stepSize).split('.')[1] || '').length);
    const shown = Number(snapped.toFixed(dec));
    if (Math.abs(shown - q) < 1e-12) return null;
    if (!(shown > 0) || (minSize != null && shown < minSize)) {
      return { blocking: true, msg: `Adım ${stepSize}'e oturmuyor ve en az ${minSize ?? stepSize} olmalı — emir tahtada tutunmaz` };
    }
    return { blocking: false, msg: `Adım ${stepSize}: borsaya ${shown} olarak gidecek` };
  })();

  // Grup kutusu iki formda da lazım (TP Only'de eksikti). Tek tanım, iki yerde
  // çağrılıyor — kopyalanırsa biri güncellenip diğeri unutulur.
  const groupField = () => (
    <div>
      <label className={`text-xs block mb-1 ${t.muted}`}
        title="Stratejinin dolumları bu gruba bağlanır. Tek seferlik emirlerden farklı olarak SÜRESİZ bir niyet kurulur — strateji durunca kaldırılır.">
        Gruba ata <span className={t.muted}>(opsiyonel)</span>
      </label>
      <select className={inp} value={(s as any).groupNo ?? 0}
        onChange={e => {
          const v = e.target.value;
          if (v === 'new') { sendWs?.({ type: 'create_lp_group', payload: { kind: 'perp' } }); return; }
          updateDraft({ groupNo: +v } as any);
        }}>
        <option value={0}>— gruba atama —</option>
        {groups.map((g: any) => (
          <option key={g.no} value={g.no}>
            Grup {g.no}{g.status === 'closed' ? ' (kapalı)' : ''} · {g.trades} işlem
          </option>
        ))}
        <option value="new">+ yeni grup aç</option>
      </select>
    </div>
  );

  const isTpOnly = strategyType === 'tp_only';
  const isPriceCondition = s.entryCondition === 'price_gt' || s.entryCondition === 'price_lt';
  const noCondition = s.entryCondition === 'none';
  const tpMode = s.tpConfig.mode ?? 'ladder';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: 'rgba(0,0,0,0.6)' }}
         onClick={e => { if (e.target === e.currentTarget) onClose(); }}>

      <div className={`${t.card} border ${t.cardBorder} rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl`}
           onClick={e => e.stopPropagation()}>

        {/* Başlık */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${t.cardBorder}`}>
          <div className="flex items-center gap-2">
            {isTpOnly
              ? <><Crosshair size={15} className="text-emerald-400" /><span className={`font-semibold text-emerald-400`}>Yeni TP Only Strateji</span></>
              : <span className={`font-semibold ${t.text}`}>Yeni Strateji — {fmtSym(symbol)}</span>
            }
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-lg ${t.muted} hover:text-red-400 transition-colors`}>
            <X size={15} />
          </button>
        </div>

        {/* Form */}
        <div className={`p-5 space-y-4 ${dark ? 'bg-[#030d1f]/60' : 'bg-blue-50/60'}`}>

          {/* Strateji Tipi seçimi */}
          <div className="flex gap-2">
            {(['standard', 'tp_only'] as const).map(type => (
              <button key={type}
                onClick={() => setStrategyType(type)}
                className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                  strategyType === type
                    ? type === 'tp_only'
                      ? 'bg-emerald-700/40 border-emerald-500 text-emerald-300'
                      : 'bg-[#4A9FE8]/20 border-[#4A9FE8] text-[#4A9FE8]'
                    : `${t.btnSecondary} opacity-60`
                }`}>
                {type === 'standard' ? 'Standart' : 'TP Only'}
              </button>
            ))}
          </div>

          {/* TP Only bilgi banner */}
          {isTpOnly && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-900/30 border border-emerald-700/40 text-emerald-300 text-xs">
              <Crosshair size={12} />
              Mevcut pozisyona TP koyar — yeni emir açmaz. Pozisyon kapandığında strateji tamamlanır.
            </div>
          )}

          {/* Temel alanlar */}
          <div className="grid gap-3 grid-cols-4">
            <div>
              <label className={`text-xs block mb-1 ${t.muted}`}>Strateji Adı</label>
              <input className={inp} value={s.name} onChange={e => updateDraft({ name: e.target.value })} />
            </div>
            <div>
              <label className={`text-xs block mb-1 ${t.muted}`}>Sembol</label>
              <select className={inp} value={s.symbol} onChange={e => updateDraft({ symbol: e.target.value })}>
                {symbolList.map(sym => <option key={sym} value={sym}>{fmtSym(sym)}</option>)}
              </select>
            </div>
            <div>
              <label className={`text-xs block mb-1 ${t.muted}`}>Yön</label>
              <select className={inp} value={s.side} onChange={e => updateDraft({ side: e.target.value as any })}>
                <option value="long">Long</option>
                <option value="short">Short</option>
              </select>
            </div>
            {isTpOnly ? groupField() : (
              <div>
                <label className={`text-xs block mb-1 ${t.muted}`}>
                  Kaldıraç {maxLev
                    ? <span className="text-[#4A9FE8]">(maks {maxLev}x)</span>
                    : <span className={t.muted}>(maks bilinmiyor)</span>}
                </label>
                <input type="number" min="1" max={maxLev ?? 100} className={inp}
                  value={s.leverage} onChange={e => updateDraft({ leverage: +e.target.value })} />
              </div>
            )}
          </div>

          {/* Standart: emir & giriş alanları */}
          {!isTpOnly && (
            <>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className={`text-xs ${t.muted}`}>
                      Emir Büyüklüğü {minSize != null && <span className="text-[#4A9FE8]">(min {minSize})</span>}
                    </label>
                    {minSize != null && maxLev != null && (
                      <button onClick={() => updateDraft({ orderSize: minSize, leverage: maxLev })}
                        className="text-[10px] px-2 py-0.5 rounded font-bold bg-[#4A9FE8]/20 text-[#4A9FE8] hover:bg-[#4A9FE8]/40">
                        Min/Maks
                      </button>
                    )}
                  </div>
                  <input type="number" step={stepSize ?? tickSize} min={minSize ?? 0} className={inp}
                    value={s.orderSize} onChange={e => updateDraft({ orderSize: +e.target.value })} />
                  {sizeWarn && (
                    <p className={`text-[10px] mt-1 ${sizeWarn.blocking ? 'text-rose-400' : 'text-amber-400/90'}`}>
                      {sizeWarn.msg}
                    </p>
                  )}
                </div>
                <div>
                  <label className={`text-xs block mb-1 ${t.muted}`}>Emir Sayısı</label>
                  <input type="number" min="1" className={inp}
                    value={s.orderCount} onChange={e => updateDraft({ orderCount: +e.target.value })} />
                </div>
                <div>
                  <label className={`text-xs block mb-1 ${t.muted}`}>Giriş Koşulu</label>
                  <select className={inp} value={s.entryCondition} onChange={e => updateDraft({ entryCondition: e.target.value })}>
                    {ENTRY_CONDITIONS.map(ec => <option key={ec.value} value={ec.value}>{ec.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={`text-xs block mb-1 ${t.muted}`}>
                    {noCondition ? 'Eşik' : isPriceCondition ? 'Fiyat ($)' : 'Eşik (%)'}
                  </label>
                  <input type="number" step={isPriceCondition ? '0.01' : '0.0001'} min="0"
                    disabled={noCondition}
                    className={`${inp} ${noCondition ? 'opacity-40 cursor-not-allowed' : ''}`}
                    value={s.entryThreshold}
                    onChange={e => updateDraft({ entryThreshold: +e.target.value })} />
                </div>
              </div>

              {minNotional != null && minNotional > 0 && (
                <p className={`text-xs ${t.muted} -mt-1 px-1`}>
                  Min. işlem tutarı: <span className={t.gold}>${minNotional} USDC</span>
                </p>
              )}

              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className={`text-xs block mb-1 ${t.muted}`}>Fiyat Tipi</label>
                  <select className={inp} value={s.priceType} onChange={e => updateDraft({ priceType: e.target.value })}>
                    {PRICE_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={`text-xs block mb-1 ${t.muted}`}>Maks. Pozisyon <span className={t.muted}>(0 = sınırsız)</span></label>
                  <input type="number" min="0" step="1" className={inp}
                    value={s.maxPositionSize ?? 0}
                    onChange={e => updateDraft({ maxPositionSize: +e.target.value })} />
                </div>
                {groupField()}
              </div>
            </>
          )}

          {/* Take Profit bölümü */}
          <div className={`border-t ${t.cardBorder} pt-4`}>
            <div className="flex items-center gap-3 mb-3">
              <label className={`text-xs font-semibold ${t.muted}`}>Take Profit</label>
              {!isTpOnly && (
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={s.tpConfig.enabled}
                    onChange={e => updateDraftTp({ enabled: e.target.checked })}
                    className="accent-[#4A9FE8]" />
                  <span className={`text-xs ${t.muted}`}>Aktif</span>
                </label>
              )}
            </div>

            {(s.tpConfig.enabled || isTpOnly) && (
              <>
                {isTpOnly && (
                  <div className="mb-3">
                    <label className={`text-xs block mb-1 ${t.muted}`}>TP Modu</label>
                    <select className={inp} value={tpMode} onChange={e => updateDraftTp({ mode: e.target.value as any })}>
                      <option value="ladder">Kademeli — N seviyede limit emir</option>
                      <option value="drip">Tetikli — Fiyat koşulunda parça parça kapat</option>
                    </select>
                  </div>
                )}

                {(!isTpOnly || tpMode === 'ladder') && (
                  <div className={`grid gap-3 ${isTpOnly ? 'grid-cols-3' : 'grid-cols-2'}`}>
                    {isTpOnly && (
                      <div>
                        <label className={`text-xs block mb-1 ${t.muted}`}>Kademe Sayısı</label>
                        <input type="number" min="1" max="20" className={inp}
                          value={s.orderCount} onChange={e => updateDraft({ orderCount: +e.target.value })} />
                      </div>
                    )}
                    <div>
                      <label className={`text-xs block mb-1 ${t.muted}`}>TP Tipi</label>
                      <select className={inp} value={s.tpConfig.type} onChange={e => updateDraftTp({ type: e.target.value as any })}>
                        <option value="percent">Yüzde (%)</option>
                        <option value="price">Sabit Fiyat ($)</option>
                      </select>
                    </div>
                    <div>
                      <label className={`text-xs block mb-1 ${t.muted}`}>
                        {isTpOnly && s.tpConfig.type === 'percent' ? 'Kademe Adımı (%)' : s.tpConfig.type === 'percent' ? 'Yüzde (%)' : 'Fiyat ($)'}
                      </label>
                      <input type="number" step="0.0001" min="0" className={inp}
                        value={s.tpConfig.value} onChange={e => updateDraftTp({ value: +e.target.value })} />
                    </div>
                  </div>
                )}

                {isTpOnly && tpMode === 'drip' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={`text-xs block mb-1 ${t.muted}`}>
                        Tetik Fiyatı ($) <span className="text-emerald-500">{s.side === 'long' ? '≥' : '≤'}</span>
                      </label>
                      <input type="number" step="0.01" min="0" className={inp}
                        value={s.tpConfig.triggerPrice ?? 0}
                        onChange={e => updateDraftTp({ triggerPrice: +e.target.value })} />
                    </div>
                    <div>
                      <label className={`text-xs block mb-1 ${t.muted}`}>Parça Büyüklüğü</label>
                      <input type="number" step="0.01" min="0" className={inp}
                        value={s.tpConfig.chunkSize ?? 0}
                        onChange={e => updateDraftTp({ chunkSize: +e.target.value })} />
                    </div>
                    <div>
                      <label className={`text-xs block mb-1 ${t.muted}`}>Fiyat Tipi</label>
                      <select className={inp} value={s.tpConfig.chunkPriceType ?? 'best_direction'}
                        onChange={e => updateDraftTp({ chunkPriceType: e.target.value })}>
                        {PRICE_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={`text-xs block mb-1 ${t.muted}`}>Maks Kapatılacak Miktar <span className={t.muted}>(0 = sınırsız)</span></label>
                      <input type="number" step="0.01" min="0" className={inp}
                        value={s.tpConfig.maxTotalQty ?? 0}
                        onChange={e => updateDraftTp({ maxTotalQty: +e.target.value })} />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Eylem butonları */}
          <div className={`flex gap-3 pt-2 border-t ${t.cardBorder}`}>
            <button onClick={confirmAdd}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-xs font-bold bg-[#4A9FE8] hover:bg-[#e5a820] text-[#061530] transition-colors">
              <Plus size={13} /> {isTpOnly ? 'TP Only Ekle' : 'Strateji Ekle'}
            </button>
            <button onClick={onClose}
              className={`px-4 py-2 rounded-lg text-xs font-medium ${t.btnSecondary} transition-colors`}>
              İptal
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
